import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/utils";
import { PosterAssetCard } from "../shared/PosterAssetCard";
import type { PosterAssetDto } from "../../../api/posterService";

/**
 * 详情页成品条(2026-07-28 视觉舱精调 W2/W3)。
 *
 * ═══ 为什么是「条」不是「网格」════════════════════════════════════════════════
 * 老版是页面**最底部**一个 2/4 列网格:用户在主画面看完一张要切下一张,得滚到 1300px
 * 以下点一下、再滚回来。组图 / 多比例套图这两条线天然是多张,每切一次滚两趟。
 * 现在贴在主画面正下方、横向排布不换行 —— 切图零滚动。
 *
 * ═══ 两条硬规矩 ═══════════════════════════════════════════════════════════
 * ① **卡按产物真实画幅**(`fitActualRatio`)。老版恒 `aspect-[3/4]`,16:9 横图被塞进竖框
 *    裁掉两边,卡上标着 16:9 画面却是竖的。多比例套图三张卡因此长得一模一样。
 * ② **副标题在卡底,不压图**。老版把比例 badge 绝对定位在图左上角,和画面内容叠在一起
 *    (用户报「场景卡显示重合」)。现在跟 #seq 同排。
 *
 * ★2026-08-02 收口:**等待态不再走这条**。出图途中由 GeneratingPanel(槽位表)接管,
 *   这里只服务终态成品。原来的占位骨架分支连同 `pendingRatios` / `pendingLabel` 入参一起摘掉 ——
 *   终态下不会有「还没回来」的槽,那段代码留着就是永远走不到的死路。
 */
/** 成品条图区统一高度(px)。卡宽由各自比例反算,一排看着是一行不是锯齿。
 *  ★148 → 96(2026-08-07):这条带子的高度是从看图台身上拿的。96 之下 6 张混画幅的套图
 *  (9:16+3:4+1:1+16:9+4:5+…)横着排 ≈ 700px,在 902 宽的看图台列里**一行全看得见**;
 *  再往下压就认不出画面了。 */
const STRIP_IMAGE_H = 96;

export function PosterResultStrip({
  assets,
  selectedId,
  onSelect,
  captionOf,
  busy = false,
  onDownload,
  className,
}: {
  /** 展示集(已排序:新图在前)。 */
  assets: PosterAssetDto[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** 每张卡底的副标题 —— 套图给比例、组图给爆点标题。返回 undefined = 不显示。 */
  captionOf?: (asset: PosterAssetDto, index: number) => string | undefined;
  busy?: boolean;
  onDownload?: (asset: PosterAssetDto) => void;
  className?: string;
}) {
  const { t } = useTranslation("poster");
  /* ★只有一张时整条不出(2026-08-07 版面重构)。
     这条带的作用是**在多张之间切**;单图任务下它是同一张图的第二次出现,
     而它占掉的 ~185px 是从看图台身上拿走的 —— 那 185px 换算到 9:16 主图上
     是画面宽度从 340 掉到 236。
     ★前提是「下载」不再只长在卡片上:看图台底栏已经有了自己的下载(AssetEditor),
     否则这一改会把单图任务唯一的下载入口一起删掉。 */
  if (assets.length <= 1) return null;

  return (
    <div className={cn("space-y-1.5", className)}>
      {/* ★ p-1 不是留白是**功能**(2026-07-28 修「第一张选中显示不全」):
          `ring-2 ring-offset-2` 是 box-shadow,画在按钮 border-box 外 4px;而
          `overflow-x: auto` 会把 `overflow-y: visible` 一并算成 auto(CSS overflow 规范),
          四边全成裁剪边,负方向溢出又不可滚动 —— 零内边距时第一张的左/上/下三边环直接被吃掉,
          只剩右边一条竖线,看着像「环长在第二张身上」。四边各留 4px 正好接住 ring 外扩。 */}
      {/* ★2026-08-05 皮肤扫查:三套皮肤都有
          `[data-skin="x"] [role="listbox"]{background/border/radius/shadow:…!important}`——
          它是冲**浮层组件**(下拉/选择面板)写的,却按 ARIA role 选元素,于是这条**内联的**
          成品缩略图带也被刷成一张浮起来的卡片(平白多出底色+边框+圆角+阴影)。
          role="listbox" 语义是对的(下面每张是 role="option"),不能为了样式把它删掉;
          用 `data-skin-plain` 声明「我不是浮层」,例外写在 index.css(同一处、带 html 前缀压特异性)。
          同族真陷阱见 VideoStoryboardGrid 的看图层(白背板+白关闭键,用户被困)。 */}
      <div
        className="flex items-end gap-3 overflow-x-auto p-1"
        role="listbox"
        data-skin-plain
        aria-label={t("taskDetail.resultsAria", { defaultValue: "成品" })}
      >
        {assets.map((a, i) => (
          <button
            key={a.id}
            type="button"
            role="option"
            aria-selected={selectedId === a.id}
            onClick={() => a.status === "SUCCESS" && onSelect(a.id)}
            /* ★ shrink-0 + 图区定高:卡宽随比例走(横图更宽、竖图更窄),一排高度齐。
               固定宽度会让 9:16 高 235px、16:9 高 74px,一排参差不齐。 */
            className={cn(
              "shrink-0 rounded-lg transition fx-press",
              a.status === "SUCCESS" &&
                selectedId === a.id &&
                "ring-2 ring-primary ring-offset-2 ring-offset-background",
              a.status === "SUCCESS" &&
                selectedId !== a.id &&
                "ring-1 ring-transparent hover:ring-primary/50"
            )}
          >
            <PosterAssetCard
              asset={a}
              busy={busy}
              fitActualRatio
              imageHeight={STRIP_IMAGE_H}
              caption={captionOf?.(a, i)}
              onDownload={a.status === "SUCCESS" && onDownload ? () => onDownload(a) : undefined}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
